import { useMemo, useState } from "react";

import { CustomProxyForm, ProxySelectFormField } from "common/components/Proxy";
import Alert from "common/components/ui/Alert";
import { Button } from "common/components/ui/Button";
import { DialogDescription, DialogTitle } from "common/components/ui/Dialog";
import { DrawerHeader } from "common/components/ui/Drawer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { WorkspaceAccount } from "common/types";

import useAccountProxyMutations from "../../datahooks/useAccountProxyMutations";
import { startAccountsBulkAction } from "../../utils";

export default function UpdateProxies({
  selectedAccounts,
  onBack,
}: {
  selectedAccounts: WorkspaceAccount[];
  onBack: () => void;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const [selectedProxy, setSelectedProxy] = useState<string>();
  const [isAddingProxy, setIsAddingProxy] = useState<boolean>(false);

  const { setAccountProxy, isSettingAccountProxy } = useAccountProxyMutations();

  const userAccounts = useMemo(
    () => selectedAccounts.filter(({ type }) => type === "user"),
    [selectedAccounts],
  );

  const rentedAccountsCount = selectedAccounts.length - userAccounts.length;

  function updateProxies() {
    startAccountsBulkAction(
      (accountId, abortSignal) =>
        setAccountProxy({
          accountId,
          proxy: selectedProxy,
          abortSignal,
        }),
      userAccounts,
      (finishedItemsCount) =>
        finishedItemsCount === userAccounts.length
          ? "Proxy assignment completed"
          : `Changing ${finishedItemsCount}/${userAccounts.length} proxies`,
    );
    onBack();
  }

  const title = "Change proxies";
  const description = `Do you want to assign new proxies to ${userAccounts.length === 1 ? userAccounts[0].full_name : `${userAccounts.length} accounts`}?`;

  return isAddingProxy ? (
    <CustomProxyForm onBack={() => setIsAddingProxy(false)} />
  ) : (
    <>
      {isDesktop ? (
        <>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="mb-4">{description}</DialogDescription>
        </>
      ) : (
        <DrawerHeader title={title} subtitle={description} onBack={onBack} />
      )}
      <ProxySelectFormField
        selectedProxy={selectedProxy}
        setSelectedProxy={setSelectedProxy}
        onAddProxy={() => setIsAddingProxy(true)}
      />

      {rentedAccountsCount > 0 && (
        <Alert className="mt-4" variant="warning">
          Any changes to proxy settings will not affect ${rentedAccountsCount}{" "}
          selected Outreach Avatar(s)
        </Alert>
      )}

      <div className="justify-between mt-8 flex">
        <Button
          variant="secondary-black"
          size={isDesktop ? "md" : "lg"}
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          size={isDesktop ? "md" : "lg"}
          disabled={!selectedProxy}
          isLoading={isSettingAccountProxy}
          onClick={updateProxies}
        >
          Save
        </Button>
      </div>
    </>
  );
}
